import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { 
  $getSelection, 
  $isRangeSelection, 
  COMMAND_PRIORITY_LOW,
  createCommand,
  $createParagraphNode,
  $getRoot
} from 'lexical';
import { useEffect } from 'react';
import { $createBoxNode, BoxNode } from '../nodes/BoxNode';
import { BoxContentNode } from '../nodes/BoxContentNode';
import { mergeRegister, $findMatchingParent } from '@lexical/utils';
import { $isLayoutContainerNode } from '../nodes/LayoutContainerNode';
import './BoxPlugin.css';

export const INSERT_BOX_COMMAND = createCommand('INSERT_BOX_COMMAND');

/**
 * 상자 하나를 지금 선택 자리에 넣는다 — editor.update 안에서 부른다(검사가 직접 부르려고 떼어 냈다).
 *
 * ⚠ 선지 묶음(layout-container) 안에는 넣지 않는다(BE-20260925 F14). 선지 칸(layout-item)은 그림자 뿌리라
 * getTopLevelElement 가 칸 안 문단에서 멈추고, 커서를 두지 않고 누르면 Lexical 이 문서 끝(⑤ 칸)을 선택으로
 * 잡아 상자가 매번 ⑤ 선지 칸 안에 들어갔다(현장 보고 「보기가 5지선다 아래」 후보, BoxPlugin.insert.test).
 * 그래서 선택이 선지 묶음 안이거나 선택이 없으면 **선지 묶음 앞**에 넣는다 — 지면의 <보기> 자리(발문 뒤·선지 앞)다.
 * 선지가 묶음이 아니라 문단으로 늘어선 문서는 가려낼 표시가 없어 종전대로 둔다(선택이 없으면 문서 끝).
 */
export function $insertBoxAtSelection(): void {
  const selection = $getSelection();
  const boxNode = $createBoxNode();
  const paragraph = $createParagraphNode();

  // BoxContentNode는 자동으로 생성됨
  boxNode.getFirstChild()?.append(paragraph);

  const layout = $isRangeSelection(selection)
    ? $findMatchingParent(selection.anchor.getNode(), $isLayoutContainerNode)
    : $getRoot().getChildren().find((n) => $isLayoutContainerNode(n)) || null;

  if (layout) {
    layout.insertBefore(boxNode);
  } else if ($isRangeSelection(selection)) {
    // 현재 선택된 노드의 위치에 Box 삽입
    const topLevelNode = selection.anchor.getNode().getTopLevelElement();
    if (topLevelNode) {
      topLevelNode.insertAfter(boxNode);
    } else {
      $getRoot().append(boxNode);
    }
  } else {
    // selection이 없으면 root의 마지막에 추가
    $getRoot().append(boxNode);
  }

  // Box 다음에 빈 paragraph 추가
  boxNode.insertAfter($createParagraphNode());

  // 커서를 Box 내부로 이동
  paragraph.select();
}

export default function BoxPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([BoxNode, BoxContentNode])) {
      throw new Error('BoxPlugin: BoxNode or BoxContentNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_BOX_COMMAND,
        () => {
          editor.update(() => {
            $insertBoxAtSelection();
          });
          return true;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor]);

  return null;
}